import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormGroup, ValidatorFn } from '@angular/forms';
import IntlTelInput, { Iti } from 'intl-tel-input';
import { parsePhoneNumberFromString as parseMin } from 'libphonenumber-js';
import { distinctUntilChanged, fromEvent, Subscription } from 'rxjs';
import { Iso2 } from 'intl-tel-input/data';

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.scss'],
  standalone: false
})
export class PhoneInputComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() form: FormGroup;

  @Input() allowDropdown: boolean;
  @Input() disabled = false;
  @Input() strict = true;
  @Input() small = false;
  @Input() large = true;
  @Input() required = false;
  @Input() nationalMode = false;
  @Input() initialCountry = 'fr';
  @Output() numberChange: EventEmitter<string> = new EventEmitter<string>();

  isInvalid = false;

  interTelInput: Iti;
  input: HTMLInputElement;
  countryChange$: Subscription;
  tempNumber: string; // used to avoid an infinite loop when typing "+3"

  private phoneChangeSubscription: Subscription;

  ngOnInit(): void {
    this.phone.addValidators(this.phoneValidator());
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.init());

    this.phoneChangeSubscription = this.phone.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
      let country = null;
      if (this.nationalMode) {
        country = this.interTelInput.getSelectedCountryData().iso2.toUpperCase();
      }

      if (this.phone.value === null || this.phone.value === undefined || this.phone.value === '') {
        this.isInvalid = false;
        this.phone.setErrors((this.phone.dirty || this.phone.touched) && this.required ? { required: this.required } : null);
      } else {
        // use parsePhoneNumberFromString() from libphonenumber-js library: less strict validation than libphonenumber used in intl-tel-input
        this.checkInvalid(this.phone.value, this.phone.dirty || this.phone.touched);
      }

      if (!this.isInvalid && this.phone.value !== '' && this.phone.value !== null) {
        this.interTelInput.setNumber(parseMin(this.phone.value, country).number);
      }
      this.emit();
    });
  }

  ngOnDestroy(): void {
    this.phone.clearValidators();
    this.phoneChangeSubscription?.unsubscribe();
    this.countryChange$?.unsubscribe();
  }

  init(): void {
    this.input = document.getElementById('mobileNumber') as HTMLInputElement;
    if (this.input) {
      this.interTelInput = IntlTelInput(this.input, {
        placeholderNumberType: 'MOBILE',
        nationalMode: this.nationalMode,
        allowDropdown: this.allowDropdown,
        formatOnDisplay: true,
        initialCountry: this.initialCountry as Iso2,
        loadUtils: () => import('intl-tel-input/utils')
      });
      this.countryChange$ = fromEvent(this.input, 'countrychange')
        .pipe(distinctUntilChanged(() => this.phone.value === this.tempNumber))
        .subscribe((res) => {
          this.tempNumber = this.phone.value;
          this.checkInvalid(this.interTelInput.getNumber());
          this.emit();
        });
      this.interTelInput.promise.then(() => {
        if (this.phone.value) {
          this.interTelInput.setNumber(this.phone.value);
          this.emit();
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.input && changes.initialCountry && changes.initialCountry.previousValue !== changes.initialCountry.currentValue) {
      this.initialCountry = changes.initialCountry.currentValue;
      this.init();
    }
  }

  private checkInvalid(number: string, dirtyOrTouched = true): void {
    let country = null;
    if (this.nationalMode) {
      country = this.interTelInput.getSelectedCountryData().iso2.toUpperCase();
    }
    const mustValidate = dirtyOrTouched && number.length > 0 && this.strict;
    this.isInvalid = mustValidate && !!this.interTelInput && (!parseMin(number, country) || !parseMin(number, country).isValid());

    this.phone.updateValueAndValidity();
  }

  private async emit(): Promise<void> {
    if (!this.isInvalid) {
      if (this.phone.value === null) {
        this.numberChange.emit(null);
      } else {
        this.numberChange.emit(this.interTelInput.getNumber());
      }
    }

    // Force phone number format on display when editing (sometimes not working)
    if (this.phone.value !== null) {
      setTimeout(() => this.interTelInput.setNumber(this.phone.value));
    }
  }

  get phone(): AbstractControl {
    return this.form.get('phone');
  }

  private phoneValidator(): ValidatorFn {
    return (_: AbstractControl) => {
      if (!this.phone.value) {
        return null;
      }
      if (!this.phone.value.startsWith('+')) {
        return { formatError: true };
      }
      return this.isInvalid ? { error: true } : null;
    };
  }
}
